/* De rekenregels van de oude routes (api/kwartieren.ts, api/dagtotalen.ts) op
 * losse meetrijen.
 *
 * Gedeeld. In het CRM rekent de database dit sinds migratie 108 zelf uit
 * (ems.dashboard_samenvatting); deze functies blijven de maat waar die uitkomst
 * tegen wordt gelegd (tests/ems/samenvatting-rpc). Wie losse rijen krijgt in
 * plaats van een samenvatting — de app-bundel (ems-app/adapter.ts) en het
 * klantportaal (portaal/adapter.ts), allebei uit de klantfuncties — zet ze
 * hiermee om naar de vorm die bouwHistorie() verwacht. alsDagtotaal() doet
 * hetzelfde voor één rij uit ems.dagtotaal (migratie 109): uit de Data API in
 * het CRM, uit client-ems-samenvatting in het portaal. Stonden tot het
 * klantportaal in crm/data/leesEms.ts, dat ze nog doorgeeft.
 */

import { eisZone } from './tijd';
import type { DagtotaalRij } from './maandrapport';

/* Het veld waarin de Solis-ingest het omvormervermogen wegschrijft. */
export const VELD_PV = 'pv_vermogen';

function float(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/* `to_char(t at time zone 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS"Z"')`: vaste lengte,
   seconden afgekapt. Het front-end vergelijkt tijdstippen als tekst. */
function utcSeconden(v: unknown): string {
  const d = v instanceof Date ? v : typeof v === 'number' ? new Date(v) : new Date(String(v));
  if (Number.isNaN(d.getTime())) throw new Error(`ongeldig tijdstip in een meetrij: ${String(v)}`);
  return `${d.toISOString().slice(0, 19)}Z`;
}

/** Tegenhanger van het antwoord van GET /api/kwartieren. */
export function alsKwartieren(rijen: any[], van: string, tot: string) {
  return {
    veld: VELD_PV, van, tot,
    rijen: rijen.map((m) => ({
      asset_id: String(m.asset_id),
      tijdstip: utcSeconden(m.tijdstip),
      veld: String(m.veld),
      eenheid: m.eenheid ?? null,
      waarde: float(m.waarde),
    })),
  };
}

/**
 * Tegenhanger van het antwoord van GET /api/dagtotalen:
 *
 *   select m.asset_id::text, a.naam as asset,
 *          to_char((m.tijdstip at time zone l.tijdzone)::date, 'YYYY-MM-DD') as dag,
 *          (sum(m.waarde) / 4.0)::float8 as kwh
 *    where m.veld = 'pv_vermogen' and m.eenheid = 'kW' ...
 *    group by 1, 2, 3 order by 3, 2
 *
 * De uitdrukking van ems.v_dagtotaal, niet de view.
 */
export function alsDagtotalen(rijen: any[], assetNamen: Map<string, string>, zone: string, van: string, tot: string) {
  eisZone(zone);
  const groepen = new Map<string, { asset_id: string; asset: string; dag: string; som: number; n: number }>();
  const dagVan = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' });
  for (const m of rijen) {
    /* Alleen de zon. Sinds er meer dan één veld in kW binnenkomt (bat_laden,
       net_import) zou een filter op alleen de eenheid het batterijvermogen als
       opwek optellen. */
    if (m.veld !== VELD_PV || m.eenheid !== 'kW') continue;
    const id = String(m.asset_id);
    const dag = dagVan.format(new Date(m.tijdstip));
    const k = `${id}|${dag}`;
    let g = groepen.get(k);
    if (!g) { g = { asset_id: id, asset: assetNamen.get(id) ?? null, dag, som: 0, n: 0 }; groepen.set(k, g); }
    /* sum() slaat null over */
    if (m.waarde !== null && m.waarde !== undefined) { g.som += Number(m.waarde); g.n++; }
  }
  const uit = [...groepen.values()]
    .map((g) => ({ asset_id: g.asset_id, asset: g.asset, dag: g.dag, kwh: g.n ? g.som / 4.0 : null }))
    .sort((a, b) => (a.dag < b.dag ? -1 : a.dag > b.dag ? 1 : String(a.asset) < String(b.asset) ? -1 : String(a.asset) > String(b.asset) ? 1 : 0));
  return { veld: VELD_PV, van, tot, rijen: uit };
}

/* Wat node-postgres van een timestamptz maakt: ISO met milliseconden. */
function isoMs(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toISOString();
}

/* Één rij uit ems.dagtotaal als floats; wat null is blijft null. De lijst per
   omvormer komt als jsonb binnen (al geparset door PostgREST, of als tekst uit
   een andere laag). */
export function alsDagtotaal(r: any): DagtotaalRij {
  let per: any = r.zon_per_omvormer ?? null;
  if (typeof per === 'string') { try { per = JSON.parse(per); } catch { per = null; } }
  return {
    dag: String(r.dag).slice(0, 10),
    zon_kwh: float(r.zon_kwh),
    zon_per_omvormer: Array.isArray(per)
      ? per.map((o: any) => ({ asset_id: String(o.asset_id), naam: String(o.naam ?? ''), kwh: float(o.kwh) ?? 0 }))
      : null,
    net_import_kwh: float(r.net_import_kwh), net_export_kwh: float(r.net_export_kwh),
    bat_laden_kwh: float(r.bat_laden_kwh), bat_ontladen_kwh: float(r.bat_ontladen_kwh),
    verbruik_kwh: float(r.verbruik_kwh),
    zon_naar_pand_kwh: float(r.zon_naar_pand_kwh), zon_naar_bat_kwh: float(r.zon_naar_bat_kwh),
    zon_naar_net_kwh: float(r.zon_naar_net_kwh), zon_naar_lader_kwh: float(r.zon_naar_lader_kwh),
    bat_naar_pand_kwh: float(r.bat_naar_pand_kwh), bat_naar_net_kwh: float(r.bat_naar_net_kwh),
    bat_naar_lader_kwh: float(r.bat_naar_lader_kwh),
    net_naar_pand_kwh: float(r.net_naar_pand_kwh), net_naar_bat_kwh: float(r.net_naar_bat_kwh),
    net_naar_lader_kwh: float(r.net_naar_lader_kwh),
    piek_kw: float(r.piek_kw), piek_tijdstip: isoMs(r.piek_tijdstip),
    soc_min: float(r.soc_min), soc_max: float(r.soc_max), soc_einde: float(r.soc_einde),
    inkoop_eur: float(r.inkoop_eur), teruglevering_eur: float(r.teruglevering_eur),
    kwartieren_gemeten: float(r.kwartieren_gemeten) ?? 0,
    volledig: r.volledig === null || r.volledig === undefined ? null : Boolean(r.volledig),
  };
}
